import clsx from 'clsx';
import { forwardRef, useEffect, useId, type HTMLAttributes } from 'react';

import {
  type DataTestId,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { useBreakpoint } from '@dynatrace/strato-components/layouts';

import { PanelContext } from './contexts/PanelContext.js';
import { useConditionalPagePanel } from './hooks/useConditionalPagePanel.js';
import { usePageContext } from './hooks/usePageContext.js';
import { pageDrawerCSS, sidebarCSS } from './Page.sty.js';
import {
  PREFERRED_WIDTHS,
  PANEL_ORDER,
  MIN_WIDTHS,
  BREAKPOINTS,
} from './Page.js';
import { usePanelsContext } from './Panel/hooks/_usePanelsContext.js';
import { Panel } from './Panel/Panel.js';
import { Resizer } from './Panel/Resizer.js';
import type { PanelSize } from './Panel/types/shared-panel-types.js';
import type {
  PagePanelSizes,
  PagePanelProps,
} from './types/shared-page-types.js';
import { KeepMounted } from '../../core/components/keep-mounted/KeepMounted.js';
import { Drawer } from '../../overlays/drawer/Drawer.js';

//#region Props
/**
 * @public
 */
export interface SidebarOwnProps
  extends WithChildren,
    Omit<HTMLAttributes<HTMLDivElement>, 'onResize' | keyof StylingProps>,
    StylingProps,
    DataTestId {
  /**
   * The desired width of the sidebar.
   * Depending on the available screen width and other panel configurations,
   * the size of the panel might not match the `preferredWidth` exactly.
   * @defaultValue 15%
   */
  preferredWidth?: PanelSize;
  /**
   * The minimum width of the sidebar in pixels.
   * @defaultValue 200
   */
  minWidth?: number;
  /**
   * Screen size below which the overlay Drawer will be shown.
   * @defaultValue 640
   */
  breakpoint?: number;
  /**
   * If the sidebar's width can be resized (by drag and drop)
   * @defaultValue true
   */
  resizable?: boolean;
  /**
   * Defines if the Sidebar content will be kept in the DOM, even if the content
   * is not shown to the user.
   * @defaultValue false
   */
  keepMounted?: boolean;
  /**
   * Callback triggered after resizing is finished with the new panel sizes of
   * all existing panels and the newly applied panel width (in px).
   */
  onResize?: (sizes: PagePanelSizes, width: number) => void;
  /**
   * Notifies the user if an action happened that should change the dismissed state
   * like clicking on the backdrop on mobile.
   * The returned state is only a recommendation and might differ from what the actual
   * state of the panel should be.
   */
  onDismissChange?: (state: boolean, reason: string) => void;
}

/**
 * @public
 */
export type SidebarProps = SidebarOwnProps & PagePanelProps;
//#endregion

/**
 * The `Sidebar` component is used inside the `Page` component for displaying content in the corresponding panel.
 * @public
 */
export const Sidebar = forwardRef<HTMLDivElement, SidebarProps>(
  (props, forwardedRef) => {
    const {
      children,
      dismissed,
      defaultDismissed,
      onDismissChange,
      resizable = true,
      breakpoint = BREAKPOINTS.sidebar,
      onResize,
      preferredWidth = PREFERRED_WIDTHS.sidebar,
      minWidth = MIN_WIDTHS.sidebar,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      keepMounted = false,
      ...remainingProps
    }: SidebarProps = props;

    const id = useId();

    const showPanel = useBreakpoint(`(min-width: ${breakpoint}px)`, true);
    const isControlled = dismissed !== undefined;

    // Register the panel with the page and set its initial state.
    useConditionalPagePanel({
      name: 'sidebar',
      defaultDismissed,
      dismissed,
      showPanel,
      breakpoint,
    });

    const { disableAnimations } = usePanelsContext();
    const { sidebarDismissed, setPanelDismissed } = usePageContext();

    useEffect(() => {
      const reason = showPanel ? 'above breakpoint' : 'below breakpoint';

      if (isControlled) {
        onDismissChange?.(dismissed, reason);
        return;
      }

      onDismissChange?.(!showPanel, reason);
      // Excluding onDismissChange to trigger the effect only if the screen size reaches the breakpoint.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPanel]);

    // The drawer handles the dismissing on its own, to ensure the proper animation, therefore, the check excludes the mobile view.
    // If the panel is controlled, we check the dismissed prop directly to make sure the panel is instantly added to state when
    // mounting. Since the default state of the panel in the context is set to 'dismissed', the layout effect in the parent panel would
    // else be triggered twice. Initially, only with the 'main' panel, and then again to add the 'sidebar'.
    if (
      ((isControlled && dismissed) || (!isControlled && sidebarDismissed)) &&
      showPanel
    ) {
      if (keepMounted === true) {
        return (
          <div style={{ display: 'none' }}>
            <KeepMounted id={id}>{children}</KeepMounted>
          </div>
        );
      }
      return null;
    }

    return (
      <PanelContext.Provider value={{ panelName: 'sidebar' }}>
        {showPanel ? (
          <>
            <Panel
              // Spreading remaining props for now to allow custom styles.
              // This will be improved as soon as our guidelines for custom styles are rolled out.
              {...remainingProps}
              onResize={(sizes, width: number) => {
                onResize?.(sizes as PagePanelSizes, width);
              }}
              ref={forwardedRef}
              name="sidebar"
              order={PANEL_ORDER.sidebar}
              preferredWidth={preferredWidth}
              minWidth={minWidth}
              resizable={resizable}
              dismissible="left"
              className={clsx(consumerClassName, sidebarCSS)}
              style={consumerStyle}
              data-testid={dataTestId}
            >
              {keepMounted ? (
                <KeepMounted id={id}>{children}</KeepMounted>
              ) : (
                children
              )}
            </Panel>
            <Resizer
              data-testid="page-sidebar-resize-handle"
              order={PANEL_ORDER.sidebar}
              resizable={resizable}
              name="sidebar-resize-handle"
            />
          </>
        ) : (
          <Drawer
            placement="left"
            width="wide"
            disableAnimations={disableAnimations}
            isDismissed={sidebarDismissed}
            onDismiss={(state, reason) => {
              // Close panel if it is uncontrolled and the user clicks on the backdrop.
              if (
                dismissed === undefined &&
                state === true &&
                (reason === 'backdrop' || reason === 'escape')
              ) {
                setPanelDismissed('sidebar', true);
              }
              onDismissChange?.(state, reason);
            }}
            className={clsx(pageDrawerCSS, consumerClassName)}
            style={consumerStyle}
            data-testid={dataTestId}
            {...remainingProps}
          >
            {keepMounted ? (
              <KeepMounted id={id}>{children}</KeepMounted>
            ) : (
              children
            )}
          </Drawer>
        )}
      </PanelContext.Provider>
    );
  },
);

(Sidebar as typeof Sidebar & { displayName: string }).displayName =
  'Page.Sidebar';
